import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';
import classNames from 'classnames';

import CART_ITEM from '../../constants/cartItems';
import { CURRENCY, CURRENCY_SYMBOLS } from '../../../../constants/currency';
import { IMAGE_SIZE_CODE } from '../../../../constants/image-size-codes';
import CartItemPhotoPreview from './CartItemPhotoPreview';
import { getAsset } from '../../../../util/assets';
import Alert from '../../../../components/global/Alert';
import { DIGITAL_DOWNLOADS_MODE } from '../../../../constants/print-packages';
import { formatPriceForDisplay } from '../../../../util/format-money';
import CustomControl from '../../../../components/global/CustomControl';
import CheckoutItemQuantityInput from './CheckoutItemQuantityInput';
import TrashIcon from '../../../../components/icons/TrashIcon';
import { getAlbumPrintPackageCartItemPrice } from '../../redux/helpers';
import CheckoutCartItemPriceDisplay from './CheckoutCartItemPriceDisplay';
import AlbumPrintPackageCartItemTaggedPhotoUpsell from './AlbumPrintPackageCartItemTaggedPhotoUpsell';
import {
  albumPrintPackageCartItemRendersTagUpsell,
  getAlbumPrintPackageCartItemUpsellPrice,
  getAlbumPrintPackageCartItemUpsellPriceCurrency,
} from './helpers';
import { getThumbBySize } from '../../../../util/photo';
import useAlbumPrintPackageCartConfigurator from '../../../../apps/pro/views/HierarchyItem/Matches/components/PurchasingOptions/components/AlbumPrintPackageOption/useAlbumPrintPackageCartConfigurator';
import PrintPackageConfigurableItemCompleteModal from '../../../../apps/pro/views/HierarchyItem/Matches/components/PurchasingOptions/components/AlbumPrintPackageOption/components/PrintPackageConfigurableItemCompleteModal';
import AccountCardAlbumCustomPrintConfiguratorModal from '../../../../apps/pro/views/HierarchyItem/Matches/components/PurchasingOptions/components/AccountCardAlbumCustomPrintOption/AccountCardAlbumCustomPrintConfigurator/modal';
import { PrintProductConfiguratorModal } from '../../../prints/components/PrintProductConfigurator';
import Button from '../../../../components/global/Button';
import {
  getPrintPackagePrintBackgrounds,
  printPackageRequiresConfiguration,
  getPrintPackageSpecialtyPrintsWithoutConfiguration,
} from '../../../../util/print-packages';
import PrintProductGroupConfiguratorModal from '../../../prints/components/PrintProductGroupConfigurator/modal';
import { getPhotoOrientation } from '../../../../apps/pro/views/HierarchyItem/Matches/components/PurchasingOptions/components/PrintProductsGroup/helpers';
import {
  getPrintPackageItemListingLabel,
  getPrintPackageItemPrintProductName,
} from '../../../../apps/pro/views/HierarchyItem/Matches/components/PurchasingOptions/components/AlbumPrintPackageOption/helpers';
import { AlbumMembershipGroupTaggedPhotosModal } from '../../../../apps/pro/views/HierarchyItem/Matches/components/PurchasingOptions/components/AlbumMembershipGroupTaggedPhotos';
import { getAlbumPrintPackageTaggedItem } from '../../../../apps/pro/views/HierarchyItem/Matches/components/PurchasingOptions/helpers';
import {
  getPrintServiceLabels,
  getPrintSizeServiceIds,
} from '../../../../util/print-product-configuration';
import { getPrintOrderServiceLabels } from '../../../../util/print-order-configuration';
import CartItemVendor from './CartItemVendor';
import { isStandardPrint } from '../../../../util/print-product';
import PrintProductSizeRoesConfiguratorModal from '../../../../apps/pro/views/HierarchyItem/Matches/components/PurchasingOptions/components/PrintProductsGroup/components/PrintProductSizeRoesConfigurator';
import WarningIcon from '../../../../components/icons/WarningIcon';
import IdentityAvatar from '../../../../containers/global/identity/IdentityAvatarContainer';
import { CUSTOM_PRINT_SUBTYPE } from '../../../../constants/pro-custom-prints';
import CardElementPhotoSelectionControl from '../../../cards/components/CardElementPhoto/CardElementPhotoSelectionControl';
import PrintPackageDigitalDownloadAccountCardModal from '../../../../apps/pro/views/HierarchyItem/Matches/components/PurchasingOptions/components/AlbumPrintPackageOption/components/PrintPackageDigitalDownloadAccountCardModal/PrintPackageDigitalDownloadAccountCardModal';
import {
  isMultiIdentityPackageMembership,
  isMultiPosePackageMembership,
} from '../../../../apps/temp/views/memberships/AlbumMembershipGroup/helpers';
import useIdentityJerseyColors from '../../../../apps/pro/views/HierarchyItem/Matches/hooks/useIdentityJerseyColors';
import PhotoSelectionOverlayModal from '../../../../apps/pro/views/HierarchyItem/Matches/components/PurchasingOptions/components/DigitalBundleOption/components/PhotoSelectionOverlayModal';

const propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.oneOf([CART_ITEM.ALBUM_PRINT_PACKAGE]).isRequired,
    albumPrintPackage: PropTypes.shape({
      id: PropTypes.string.isRequired,
      print_package: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        price: PropTypes.shape({
          value: PropTypes.number.isRequired,
          currency: PropTypes.oneOf(Object.values(CURRENCY)),
        }).isRequired,
      }).isRequired,
    }).isRequired,
    photo: PropTypes.shape({
      id: PropTypes.string.isRequired,
      thumbnail_urls: PropTypes.arrayOf(
        PropTypes.shape({
          url: PropTypes.string.isRequired,
          width: PropTypes.number.isRequired,
          height: PropTypes.number.isRequired,
          size_code: PropTypes.oneOf(Object.values(IMAGE_SIZE_CODE)).isRequired,
        }).isRequired,
      ).isRequired,
    }).isRequired,
    extendedPrintPackageItems: PropTypes.arrayOf(
      PropTypes.shape({
        photo: PropTypes.shape({
          id: PropTypes.string.isRequired,
        }),
        printPackageItem: PropTypes.shape({
          id: PropTypes.string.isRequired,
        }),
      }),
    ),
    quantity: PropTypes.number.isRequired,
    includeDigitalDownload: PropTypes.bool.isRequired,
  }).isRequired,
  pricing: PropTypes.shape({
    price: PropTypes.object,
    discount_price: PropTypes.object,
  }),
  loading: PropTypes.bool,
  onRemove: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  openModal: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
};

const PHOTO_SELECTION_STEP = {
  NONE: 'NONE',
  UPSELL: 'UPSELL',
  PHOTO_SELECT: 'PHOTO_SELECT',
};

const AlbumPrintPackageCartItem = ({
  item,
  pricing,
  loading,
  onRemove,
  onUpdate,
  openModal,
  closeModal,
  watermarkSettings,
}) => {
  const {
    albumPrintPackage,
    quantity,
    identity,
    photo,
    albumMembershipGroup,
    configurableItems: itemConfigurableItems,
    prePaid: isPrepaid,
    isAllPhotos,
    isGreenScreenEnabled,
    selectedPrintPackageItems,
  } = item;

  const { jerseyColors } = useIdentityJerseyColors({
    identityId: identity?.id,
    albumFolderId:
      albumMembershipGroup?.album_folder?.id ??
      albumMembershipGroup?.album?.root_album_folder?.id,
  });

  const printPackage = albumPrintPackage.print_package;
  const orderServiceLabels = getPrintOrderServiceLabels(
    printPackage.print_order_configuration,
  );
  const digitalUpsellPrice = getAlbumPrintPackageCartItemUpsellPrice(item);
  const digitalUpsellPriceCurrency =
    getAlbumPrintPackageCartItemUpsellPriceCurrency(item);
  const price = getAlbumPrintPackageCartItemPrice(item);
  const rendersTagUpsell = albumPrintPackageCartItemRendersTagUpsell(item);
  const extendedPrintPackageItem = item.extendedPrintPackageItems?.[0];
  const allowMultiPose =
    isMultiPosePackageMembership(albumMembershipGroup) ?? false;
  const allowMultiIdentityPhoto =
    isMultiIdentityPackageMembership(albumMembershipGroup) ?? false;

  const [configurableItemsListModalProps, setConfigurableItemsListModalProps] =
    useState(null);

  const [photoSelectionStep, setPhotoSelectionStep] = useState(
    PHOTO_SELECTION_STEP.NONE,
  );

  const [digitalDownloadPhotoModalOpen, setDigitalDownloadPhotoModalOpen] =
    useState(false);

  const [
    digitalDownloadAccountCardModalOpen,
    setDigitalDownloadAccountCardModalOpen,
  ] = useState(false);

  const overriddenPrintPackageItem =
    getAlbumPrintPackageTaggedItem(albumPrintPackage);

  const albumFolderTag = overriddenPrintPackageItem?.album_folder_tag;

  const {
    activeConfigurableItem,
    onItemConfigured,
    onItemsConfigured,
    onConfigurationStart,
    onConfigurationCancel,
    configurableItems,
    onConfigurableItemUpdate,
    activeConfig,
  } = useAlbumPrintPackageCartConfigurator({
    albumPrintPackage,
    albumPrintPackageCartItem: item,
    onItemConfiguredComplete: ({
      completeConfigurableItem,
      remainingConfigurableItems,
    }) => {
      if (remainingConfigurableItems.length > 0) {
        setConfigurableItemsListModalProps({
          printPackage,
          completeConfigurableItem,
          remainingConfigurableItems,
        });
      }
    },
    onUpdateCartItem: onUpdate,
    allowMultiPose,
  });

  const remainingItems = configurableItems?.length ?? 0;
  const standardItems = (configurableItems ?? []).filter(configurableItem =>
    isStandardPrint(
      configurableItem?.print_product_configuration?.print_product,
    ),
  );
  const singlePhotoSpecialtyItems =
    getPrintPackageSpecialtyPrintsWithoutConfiguration(
      albumPrintPackage,
      allowMultiPose,
    ).filter(
      specialtyPrintItem =>
        !selectedPrintPackageItems?.length ||
        !!selectedPrintPackageItems.find(
          ({ id }) => id === specialtyPrintItem.id,
        ),
    );
  const nonConfigurableItems =
    albumPrintPackage?.print_package?.items?.records?.filter(
      printPackageItem =>
        !isPrepaid &&
        (!!printPackageItem.digital_bundle ||
          printPackageItem.grants_purchased_identity),
    );
  const printBackgrounds = isGreenScreenEnabled
    ? getPrintPackagePrintBackgrounds(
        albumPrintPackage,
        albumMembershipGroup,
        jerseyColors,
      )
    : [];

  const prepaidThumbClasses = classNames({
    'purchasing-option-item-thumb': true,
    'thumb-image-fill': !!printPackage?.preview_asset_image?.url && isPrepaid,
  });

  return (
    <div
      data-testid="checkout-album-print-package-cart-item"
      className="checkout-cart-item checkout-album-print-package-cart-item"
    >
      <div className="checkout-cart-item-content">
        <div className="checkout-cart-item-figure">
          {isPrepaid ? (
            <Fragment>
              <div className={prepaidThumbClasses}>
                <img
                  src={
                    printPackage?.preview_asset_image?.url ??
                    getAsset('/icon-package-3d.svg')
                  }
                  alt=""
                  className="pr-2"
                />
              </div>
              <IdentityAvatar identity={identity} />
            </Fragment>
          ) : (
            <CartItemPhotoPreview
              photo={item.photo}
              watermarkSettings={watermarkSettings}
            >
              <span className="checkout-cart-item-photo-icon">
                <img
                  src={
                    printPackage?.preview_asset_image?.url ??
                    getAsset('/icon-package-3d.svg')
                  }
                  alt=""
                />
              </span>
              {extendedPrintPackageItem?.photo && (
                <span className="checkout-cart-item-photo-img-secondary">
                  <img
                    alt=""
                    src={
                      getThumbBySize(
                        extendedPrintPackageItem.photo.thumbnail_urls,
                        IMAGE_SIZE_CODE.SMALL2X,
                      )?.url
                    }
                  />
                </span>
              )}
            </CartItemPhotoPreview>
          )}
        </div>

        <div className="checkout-cart-item-info">
          <span className="checkout-cart-item-title d-block">
            {printPackage.name}
          </span>

          <CheckoutCartItemPriceDisplay
            clientPrice={price}
            pricing={pricing}
            loading={loading}
          />
          <CartItemVendor cartItem={item} />

          <div className="checkout-cart-item-info-actions mt-2">
            <CheckoutItemQuantityInput
              onChange={value =>
                onUpdate(item.id, {
                  quantity: value,
                })
              }
              value={quantity}
              label=""
            />

            <button
              className="checkout-cart-item-btn-remove"
              onClick={() => onRemove(item.id)}
            >
              <TrashIcon color="#fe5851" size={18} />
            </button>
          </div>
        </div>
      </div>
      {remainingItems > 0 && item && !isPrepaid && (
        <Button
          btnStyle="link"
          className="purchasing-options-item-btn mt-1"
          onClick={onConfigurationStart}
        >
          <WarningIcon size={16} color="#fe5851" className="mr-1" /> Configure (
          {remainingItems}) more {pluralize('item', remainingItems)}
        </Button>
      )}
      {singlePhotoSpecialtyItems.length > 0 && !isPrepaid && (
        <div className="mt-1">
          {singlePhotoSpecialtyItems.map(printPackageItem => {
            const printPackageItemName =
              printPackageItem?.type?.name ??
              printPackageItem?.custom_print?.name ??
              getPrintPackageItemPrintProductName(printPackageItem);

            return (
              <div
                key={printPackageItem.id}
                className="d-flex align-self-start w-100 mt-1"
              >
                <span className="text-sm text-bold text-dark text-truncate">
                  {printPackageItemName}
                </span>
              </div>
            );
          })}
        </div>
      )}
      {printPackageRequiresConfiguration(printPackage, allowMultiPose) &&
        remainingItems === 0 &&
        !isPrepaid && (
          <div className="mt-1">
            {(itemConfigurableItems ?? []).map(
              printPackageItemConfiguration => {
                const { printPackageItem } = printPackageItemConfiguration;

                if (!printPackageItem) {
                  return null;
                }

                const printPackageItemName = (() => {
                  if (printPackageItem?.digital_bundle) {
                    return (
                      <>
                        Digital Photo Bundle:{' '}
                        {pluralize(
                          'Photo',
                          printPackageItem?.digital_bundle?.photos_count ?? 1,
                          true,
                        )}
                        <br />
                        (Preferred digital photos are chosen after checkout)
                      </>
                    );
                  }

                  return (
                    printPackageItem?.type?.name ??
                    printPackageItem?.custom_print?.name ??
                    getPrintPackageItemListingLabel(printPackageItem)
                  );
                })();

                const itemServiceLabels = getPrintServiceLabels(
                  printPackageItem.print_product_configuration,
                );

                const itemServiceLabelsStr =
                  itemServiceLabels.length > 0
                    ? ` – Includes ${itemServiceLabels.join(', ')}.`
                    : '';

                return (
                  <div
                    key={printPackageItem.id}
                    className="d-flex align-self-start w-100 mt-1"
                  >
                    <Button
                      key={printPackageItem.id}
                      btnSize="sm"
                      btnStyle="link"
                      onClick={() => {
                        onConfigurableItemUpdate(printPackageItemConfiguration);
                      }}
                      className="text-extra-bold text-nowrap"
                    >
                      {printPackageItem.print_product_configuration
                        ?.print_product?.card?.id
                        ? 'View & Edit'
                        : 'Edit'}
                    </Button>
                    <span className="text-sm text-bold text-dark text-truncate">
                      &nbsp;&ndash;&nbsp;{printPackageItemName}
                      {itemServiceLabelsStr}
                    </span>
                  </div>
                );
              },
            )}
          </div>
        )}
      {nonConfigurableItems?.length > 0 && !isPrepaid && (
        <div className="mt-1">
          {(nonConfigurableItems ?? []).map(printPackageItem => {
            const printPackageItemName = (() => {
              if (printPackageItem?.grants_purchased_identity) {
                return 'Digital Photo Pass';
              }

              if (printPackageItem?.digital_bundle) {
                return (
                  <>
                    Digital Photo Bundle:{' '}
                    {pluralize(
                      'Photo',
                      printPackageItem?.digital_bundle?.photos_count ?? 1,
                      true,
                    )}
                    <br />
                    (Preferred digital photos are chosen after checkout)
                  </>
                );
              }

              return (
                printPackageItem?.type?.name ??
                printPackageItem?.custom_print?.name ??
                getPrintPackageItemPrintProductName(printPackageItem)
              );
            })();

            const itemServiceLabels = getPrintServiceLabels(
              printPackageItem.print_product_configuration,
            );

            const itemServiceLabelsStr =
              itemServiceLabels.length > 0
                ? ` – Includes ${itemServiceLabels.join(', ')}.`
                : '';

            return (
              <div
                key={printPackageItem.id}
                className="d-flex align-self-start w-100 mt-1"
              >
                <span className="text-sm text-bold text-dark text-truncate">
                  {printPackageItemName}
                  {itemServiceLabelsStr}
                </span>
              </div>
            );
          })}
        </div>
      )}
      {(printPackage.digital_downloads_mode === DIGITAL_DOWNLOADS_MODE.UPSELL ||
        printPackage.digital_downloads_mode ===
          DIGITAL_DOWNLOADS_MODE.INCLUDED ||
        rendersTagUpsell ||
        extendedPrintPackageItem ||
        orderServiceLabels.length > 0) && (
        <div className="membership-cart-item-footer">
          {extendedPrintPackageItem && (
            <Alert
              type="info"
              className="w-100 d-flex align-items-center justify-content-between text-sm mb-1"
            >
              Includes {albumFolderTag.name} &ndash;{' '}
              {getPrintPackageItemListingLabel(overriddenPrintPackageItem)}
              <Button
                btnStyle="link"
                className="text-extra-bold"
                onClick={() =>
                  setPhotoSelectionStep(PHOTO_SELECTION_STEP.PHOTO_SELECT)
                }
              >
                Edit
              </Button>
            </Alert>
          )}

          {((!isPrepaid &&
            printPackage.digital_downloads_mode ===
              DIGITAL_DOWNLOADS_MODE.UPSELL) ||
            printPackage.digital_downloads_mode ===
              DIGITAL_DOWNLOADS_MODE.INCLUDED ||
            orderServiceLabels.length > 0) && (
            <Alert type="info" className="text-sm m-0">
              {printPackage.digital_downloads_mode ===
                DIGITAL_DOWNLOADS_MODE.UPSELL &&
                !isPrepaid && (
                  <button
                    className="checkout-album-print-package-cart-item-mode-btn"
                    onClick={() => {
                      if (item.includeDigitalDownload) {
                        onUpdate(item.id, {
                          includeDigitalDownload: !item.includeDigitalDownload,
                          digitalDownloadAccountCard: undefined,
                        });
                      } else {
                        onUpdate(item.id, {
                          includeDigitalDownload: !item.includeDigitalDownload,
                        });
                      }
                    }}
                  >
                    Add hi&ndash;res download for{' '}
                    {CURRENCY_SYMBOLS[digitalUpsellPriceCurrency ?? 'USD']}
                    {formatPriceForDisplay(digitalUpsellPrice, true)}
                    <CustomControl
                      id={albumPrintPackage.id}
                      type="checkbox"
                      size="lg"
                      readOnly
                      labelSrOnly
                      checked={item.includeDigitalDownload}
                      label="Include hi-res download"
                      className="pointer-events-none"
                    />
                  </button>
                )}

              {printPackage.digital_downloads_mode ===
                DIGITAL_DOWNLOADS_MODE.INCLUDED && (
                <span>Includes hi&ndash;res download for this photo</span>
              )}
              {!isPrepaid &&
                allowMultiPose &&
                (printPackage.digital_downloads_mode ===
                  DIGITAL_DOWNLOADS_MODE.INCLUDED ||
                  item.includeDigitalDownload) && (
                  <Button
                    onClick={() => setDigitalDownloadPhotoModalOpen(true)}
                    btnStyle="link"
                    btnSize="sm"
                    className={item.includeDigitalDownload ? '' : 'ml-md-5'}
                  >
                    Change photo
                  </Button>
                )}
              {orderServiceLabels.length > 0 && (
                <p className="mb-0">Includes {orderServiceLabels.join(', ')}</p>
              )}
            </Alert>
          )}

          {rendersTagUpsell && (
            <AlbumPrintPackageCartItemTaggedPhotoUpsell
              identity={identity}
              albumMembershipGroup={albumMembershipGroup}
              albumPrintPackageCartItem={item}
              closeModal={closeModal}
              openModal={openModal}
              onCartItemUpdate={onUpdate}
            />
          )}
        </div>
      )}
      <AccountCardAlbumCustomPrintConfiguratorModal
        forceOpen={
          !!activeConfigurableItem?.custom_print &&
          activeConfigurableItem?.custom_print?.subtype !==
            CUSTOM_PRINT_SUBTYPE.SPECIALTY_PRINT
        }
        albumCustomPrint={{
          custom_print: activeConfigurableItem?.custom_print,
        }}
        onDismiss={onConfigurationCancel}
        onFinished={({ accountCard }) => {
          onItemConfigured({ accountCard });
        }}
        deleteAccountCardOnDismiss={!activeConfig}
        buttonText="Done"
        identity={identity}
        albumMembershipGroup={albumMembershipGroup}
        defaultAccountCard={activeConfig?.accountCard}
        isAllPhotos={isAllPhotos}
        watermarkSettings={watermarkSettings}
      />
      <CardElementPhotoSelectionControl
        uploadDisabled
        title={`Select ${pluralize(
          'photo',
          activeConfigurableItem?.custom_print?.photos_no,
          true,
        )} for: ${activeConfigurableItem?.custom_print?.name}`}
        photosContext={{
          album_id:
            albumMembershipGroup &&
            (albumMembershipGroup.is_album
              ? albumMembershipGroup.id
              : undefined),
          album_folder_id:
            albumMembershipGroup &&
            (!albumMembershipGroup.is_album
              ? albumMembershipGroup.id
              : undefined),
          root_album_folder_id:
            albumMembershipGroup && albumMembershipGroup.is_album
              ? albumMembershipGroup.album.root_album_folder?.id
              : undefined,
          // We rely on a resolver that the membershipGroup would have by
          // default when we don't create a pseudo membership (identities).
          // This allows us to actually discern if this is a membership or not.
          is_album_membership_group: !!albumMembershipGroup?.identities,
          is_all_photos: isAllPhotos,
          identity_id: allowMultiIdentityPhoto ? undefined : identity?.id,
        }}
        forceOpen={
          !!activeConfigurableItem?.custom_print &&
          activeConfigurableItem?.custom_print?.subtype ===
            CUSTOM_PRINT_SUBTYPE.SPECIALTY_PRINT
        }
        onClose={onConfigurationCancel}
        maxPhotosCount={activeConfigurableItem?.custom_print?.photos_no}
        currentSelectedPhotos={
          activeConfig?.photos
            ? activeConfig.photos.map(
                ({ photo: selectedPhoto }) => selectedPhoto,
              )
            : [photo]
        }
        currentSelectedPhoto={
          allowMultiPose &&
          activeConfigurableItem?.custom_print?.photos_no === 1 &&
          activeConfig?.photos?.length === 1
            ? activeConfig?.photos?.[0]?.photo
            : undefined
        }
        onChange={photos => {
          onItemConfigured({
            photos: photos?.id
              ? [{ photo: photos, position: 0 }]
              : photos.map((selectedPhoto, index) => ({
                  photo: selectedPhoto,
                  position: index,
                })),
          });
        }}
        watermarkSettings={watermarkSettings}
      />
      <PhotoSelectionOverlayModal
        forceOpen={activeConfigurableItem?.digital_bundle}
        onClose={onConfigurationCancel}
        maxPhotos={activeConfigurableItem?.digital_bundle?.photos_count}
        albumMembershipGroupId={
          isAllPhotos ? undefined : albumMembershipGroup?.id
        }
        albumId={isAllPhotos ? albumMembershipGroup?.id : undefined}
        currentSelectedPhotos={activeConfigurableItem?.photos ?? []}
        identityId={allowMultiIdentityPhoto ? undefined : identity?.id}
        onComplete={({ photos }) => onItemConfigured({ photos })}
        watermarkSettings={watermarkSettings}
      />
      <PhotoSelectionOverlayModal
        forceOpen={digitalDownloadPhotoModalOpen}
        onClose={() => setDigitalDownloadPhotoModalOpen(false)}
        maxPhotos={1}
        albumMembershipGroupId={
          isAllPhotos ? undefined : albumMembershipGroup?.id
        }
        albumId={isAllPhotos ? albumMembershipGroup?.id : undefined}
        currentSelectedPhotos={[photo]}
        identityId={allowMultiIdentityPhoto ? undefined : identity?.id}
        onComplete={({ photos }) => onUpdate(item.id, { photo: photos[0] })}
        watermarkSettings={watermarkSettings}
      />
      {activeConfigurableItem?.print_product_configuration && (
        <>
          {isStandardPrint(
            activeConfigurableItem?.print_product_configuration?.print_product,
          ) && !activeConfig ? (
            <PrintProductGroupConfiguratorModal
              forceOpen
              printProducts={standardItems.map(printProductItem => ({
                id: printProductItem?.id,
                printProductId:
                  printProductItem?.print_product_configuration.print_product
                    .id,
                printProductName:
                  printProductItem?.print_product_configuration.print_product
                    .name,
                printProductSizeId:
                  printProductItem?.print_product_configuration
                    .print_product_size.id,
                printProductSizeName:
                  printProductItem?.print_product_configuration
                    .print_product_size.print_size.name,
                printProductSizeServiceIds: getPrintSizeServiceIds(
                  printProductItem?.print_product_configuration,
                ),
                orientation: getPhotoOrientation(photo),
                // defaultAccountCard: technically supported, but we're not editing
                // on this modal as of now. If we do, we'll need to get each config
                // from the cart Item.
              }))}
              photo={photo}
              onClose={() => {
                onConfigurationCancel();
              }}
              onComplete={configurations => {
                onItemsConfigured(configurations);
              }}
              buttonText="Done"
              albumMembershipGroup={albumMembershipGroup}
              identity={identity}
              printBackgrounds={printBackgrounds}
              watermarkSettings={watermarkSettings}
              isAllPhotos={isAllPhotos}
              allowMultiPose={printPackage?.is_multi_pose}
              allowMultiIdentity={printPackage?.is_multi_identity}
            />
          ) : (
            <>
              <PrintProductConfiguratorModal
                forceOpen={
                  activeConfigurableItem?.print_product_configuration
                    ?.print_product?.card?.id
                }
                printProductId={
                  activeConfigurableItem?.print_product_configuration
                    ?.print_product.id
                }
                printProductSizeId={
                  activeConfigurableItem?.print_product_configuration
                    ?.print_product_size.id
                }
                printProductSizeServiceIds={getPrintSizeServiceIds(
                  activeConfigurableItem?.print_product_configuration,
                )}
                orientation={getPhotoOrientation(photo)}
                photo={photo}
                onClose={() => {
                  onConfigurationCancel();
                }}
                onComplete={({ accountCard }) => {
                  onItemConfigured({ accountCard });
                }}
                defaultAccountCard={activeConfig?.accountCard}
                buttonText="Done"
                controlsDisabled
                albumMembershipGroup={albumMembershipGroup}
                identity={identity}
                printBackgrounds={printBackgrounds}
                watermarkSettings={watermarkSettings}
              />
              <PrintProductSizeRoesConfiguratorModal
                isInCart
                forceOpen={
                  !activeConfigurableItem?.print_product_configuration
                    ?.print_product?.card?.id
                }
                printProduct={
                  activeConfigurableItem?.print_product_configuration
                    ?.print_product
                }
                printProductSize={
                  activeConfigurableItem?.print_product_configuration
                    ?.print_product_size
                }
                defaultConfiguration={activeConfig?.roesConfiguration}
                photo={photo}
                onComplete={({ roesConfiguration }) => {
                  onItemConfigured({ roesConfiguration });
                }}
                albumMembershipGroup={albumMembershipGroup}
                isAllPhotos={isAllPhotos}
                onClose={() => onConfigurationCancel()}
                watermarkSettings={watermarkSettings}
              />
            </>
          )}
        </>
      )}
      {!!configurableItemsListModalProps && (
        <PrintPackageConfigurableItemCompleteModal
          forceOpen
          onCheckout={() => {
            // Do nothing on checkout
          }}
          onConfigureNextItem={onConfigurationStart}
          {...configurableItemsListModalProps}
          onClose={() => {
            setConfigurableItemsListModalProps(null);
          }}
        />
      )}
      {isGreenScreenEnabled && !isPrepaid && (
        <PrintPackageDigitalDownloadAccountCardModal
          forceOpen={digitalDownloadAccountCardModalOpen}
          accountCards={(item?.configurableItems ?? [])
            .filter(configItem => !!configItem?.config?.accountCard?.id)
            .map(({ config }) => config.accountCard)}
          onAccountCardSelected={accountCard => {
            onUpdate(item.id, {
              includeDigitalDownload: true,
              digitalDownloadAccountCard: accountCard,
            });
            setDigitalDownloadAccountCardModalOpen(false);
          }}
        />
      )}
      {overriddenPrintPackageItem && (
        <AlbumMembershipGroupTaggedPhotosModal
          forceOpen={photoSelectionStep === PHOTO_SELECTION_STEP.PHOTO_SELECT}
          identity={identity}
          albumMembershipGroup={albumMembershipGroup}
          albumFolderTag={albumFolderTag}
          overriddenPrintPackageItem={overriddenPrintPackageItem}
          onPhotoSelect={({ photo: selectedPhoto, accountCard }) => {
            onUpdate(item.id, {
              extendedPrintPackageItems: [
                {
                  printPackageItem: overriddenPrintPackageItem,
                  photo: selectedPhoto,
                  accountCard,
                },
              ],
            });
            setPhotoSelectionStep(PHOTO_SELECTION_STEP.NONE);
          }}
          onClose={() => setPhotoSelectionStep(PHOTO_SELECTION_STEP.NONE)}
          photoIsOptional={overriddenPrintPackageItem.is_optional}
        />
      )}
    </div>
  );
};

AlbumPrintPackageCartItem.propTypes = propTypes;

export default AlbumPrintPackageCartItem;
